export type TSurveyModality = 'in_person' | 'live_online'

export const MODALITY_LABELS: Record<TSurveyModality, string> = {
  in_person: 'Presencial',
  live_online: 'Online ao Vivo'
}

/**
 * Classificação do respondente pela nota da pergunta de NPS. 'passive' é o "neutro" da tela: o
 * valor é o mesmo que a API e os cards de NPS (`passives`) usam.
 */
export type TSurveyNpsClass = 'detractor' | 'passive' | 'promoter'

export const NPS_CLASS_LABELS: Record<TSurveyNpsClass, string> = {
  detractor: 'Detrator',
  passive: 'Neutro',
  promoter: 'Promotor'
}

export type TSurveyQuestionType = 'scale' | 'choice' | 'text'

/** Recorte do relatório: uma pesquisa, ou 'all' — a aba Geral, que soma todas. */
export type TSurveyScope = number | 'all'

export interface ISurveyListItem {
  id: number
  name: string
  slug: string
  is_active: boolean
  responses_count: number
}

/** Pergunta da pesquisa. As de texto só existem aqui: não têm agregado no relatório. */
export interface ISurveyQuestion {
  id: number
  statement: string
  short_label: string
  type: TSurveyQuestionType
  is_required: boolean
  is_nps: boolean
  position: number
}

export interface ISurveyNps {
  question_id: number
  short_label: string
  /** Nulo quando ninguém respondeu a pergunta de recomendação sob os filtros atuais. */
  score: number | null
  answers_count: number
  promoters: number
  passives: number
  detractors: number
}

export interface ISurveyScaleQuestion {
  question_id: number
  short_label: string
  statement: string
  is_nps: boolean
  /** NPS da própria pergunta. Nulo quando 'answers_count' é 0 — zero seria "empate", não "sem dado". */
  score: number | null
  /** Nulo quando 'answers_count' é 0 — zero é uma nota, "sem dados" não é. */
  average: number | null
  answers_count: number
  promoters: number
  passives: number
  detractors: number
}

export interface ISurveyChoiceOption {
  option: string
  count: number
  percentage: number
}

export interface ISurveyChoiceQuestion {
  question_id: number
  short_label: string
  statement: string
  answers_count: number
  distribution: ISurveyChoiceOption[]
}

export interface ISurveyReport {
  /** Na aba Geral vem o recorte sintético: id 'all', nome 'Geral' e sem slug de formulário. */
  survey: { id: TSurveyScope; name: string; slug: string | null; is_active: boolean }
  total_responses: number
  /** Todas as perguntas, na ordem de 'position' — é daqui que saem as colunas da listagem. */
  questions: ISurveyQuestion[]
  /** Nulo quando a pesquisa não tem pergunta marcada como NPS. */
  nps: ISurveyNps | null
  scale_questions: ISurveyScaleQuestion[]
  choice_questions: ISurveyChoiceQuestion[]
}

export interface ISurveyAnswer {
  id: number
  survey_response_id: number
  survey_question_id: number
  /** Preenchido nas perguntas de nota; texto livre e opção escolhida vão em 'value_text'. */
  value_numeric: number | null
  value_text: string | null
}

export interface ISurveyResponseRow {
  id: number
  survey_id: number
  /** Nulo quando o aluno respondeu anonimamente. */
  respondent_name: string | null
  is_anonymous: boolean
  course_id: number | null
  course_name: string
  teacher_id: number | null
  teacher_name: string | null
  modality: TSurveyModality
  created_at: string
  updated_at: string
  /** Pergunta opcional deixada em branco não aparece na lista. */
  answers: ISurveyAnswer[]
}

export interface ISurveyResponsesPage {
  current_page: number
  per_page: number
  /** Total da pesquisa sem filtro, para a tela mostrar o quanto o filtro recortou. */
  total: number
  total_filtered: number
  data: ISurveyResponseRow[]
}

export interface ISurveyFilters {
  course_id: string
  /** Vem do curso, numa relação N:N — vazio = todas as áreas. */
  knowledge_area_id: string
  teacher_id: string
  /** Vazio = todas as modalidades. */
  modality: TSurveyModality | ''
  /** Pela nota da pergunta de NPS; resposta sem essa nota some do recorte. Vazio = todas. */
  nps_class: TSurveyNpsClass | ''
  date_from: string
  date_to: string
}
